import { FormikHelpers } from "formik";
import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { isApiError } from "../../api/common";
import authApi from "../../api/public";
import { publicUserAPI } from "../../api/publicUser";
import twoFaApi from "../../api/twoFa";
import { useAuthErrorPopup } from "../../common/hooks";
import { useGeneralErrorHandlerCreator } from "../../common/hooks/useGeneralErrorHandler";
import { PNG_HEADER } from "../../constants/files";
import { FormSubmissionHandler } from "../../types/common";
import { composeErrorHandlers, GenericError } from "../../utils/errorHandling";
import AccountSuccessRedirecting from "./components/AccountSuccessRedirecting";
import ResetPasswordForm, {
    ResetPasswordFormValues,
    ResetPasswordMode,
} from "./components/ResetPasswordForm";
import SetupOtpForm from "./components/SetupOtpForm";
import PlatformAgreementForm, {
    PlatformAgreementFormValues,
} from "./components/PlatformAgreementForm";

enum CreateAccountState {
    Agreement,
    Create,
    SetupOtp,
    Redirecting,
}

const CreateAccountView = (): JSX.Element => {
    const useGeneralError = useGeneralErrorHandlerCreator();
    const [state, setState] = useState<CreateAccountState>(
        CreateAccountState.Agreement,
    );
    const [require, isRequire] = useState<boolean>(false);
    const [totpSecret, setTotpSecret] = useState<{
        base64: string;
        qrCode: string;
    }>();
    useAuthErrorPopup();
    const { token } = useParams<{ token: string }>();

    const check = useCallback(
        async () => {
            if (token) {
                try {
                    const require = await publicUserAPI.agreeCheck(token);
                    if (!require) {
                        setState(CreateAccountState.Create);
                    } else {
                        isRequire(true);
                        setState(CreateAccountState.Agreement);
                    }
                } catch (err) {
                    console.log(err)
                    return;
                }
        }
        }, [token]
    );

    useEffect(() => {
        check();
        return () => {
            setState(CreateAccountState.Agreement);
        }
    }, [check]);

    const handleSubmit: FormSubmissionHandler<ResetPasswordFormValues> = async (
        values,
        helper,
    ) => {
        helper.setSubmitting(true);
        if (token) {
            try {
                const res = await publicUserAPI.setupPassword(
                    token,
                    values.password,
                );
                setState(CreateAccountState.SetupOtp);
                login(res.email, values.confirmedPassword, helper);
            } catch (e) {
                composeErrorHandlers(useGeneralError)(e as GenericError);
            }
        }
        helper.setSubmitting(false);
    };

    const confirmOtp: FormSubmissionHandler<{ otp: string }> = async (
        values,
        { setErrors, setSubmitting },
    ) => {
        setSubmitting(true);
        try {
            await twoFaApi.confirm(Number.parseFloat(values.otp));
            setState(CreateAccountState.Redirecting);
        } catch (e) {
            composeErrorHandlers((next) => (e) => {
                if (isApiError(e)) {
                    setErrors({
                        otp: "You didn't enter the expected verification code. Please try again.",
                    });
                } else {
                    next(e);
                }
            })(e as GenericError);
        }
        setSubmitting(false);
    };

    const confirmAgreement: FormSubmissionHandler<PlatformAgreementFormValues> = async (
        values,
        { setSubmitting },
    ) => {
        setSubmitting(true);
        try {
            await publicUserAPI.agreeSign(token);
            setState(CreateAccountState.Create);
        } catch (e) {
            composeErrorHandlers((next) => (e) => {
                if (isApiError(e)) {
                } else {
                    next(e);
                }
            })(e as GenericError);
        }
        setSubmitting(false);
    };

    const login = async (
        email: string,
        password: string,
        { setStatus, setSubmitting }: FormikHelpers<any>,
    ) => {
        setSubmitting(true);
        try {
            const res = await authApi.login({
                username: email,
                password: password,
            });
            if (res.mfaRequired) {
                if (res.mfaSecret) {
                    setState(CreateAccountState.SetupOtp);
                    setTotpSecret({
                        base64: res.mfaSecret.base64,
                        qrCode: `${PNG_HEADER}${res.mfaSecret.qrcodePngBase64}`,
                    });
                } else {
                    throw new Error("Cant login");
                }
            } else {
                setState(CreateAccountState.Redirecting);
            }
        } catch (e) {
            composeErrorHandlers((next) => (e) => {
                if (isApiError(e) && e.status === 403) {
                    setStatus("Cant login.");
                } else {
                    next(e);
                }
            })(e as GenericError);
        }
        setSubmitting(false);
    };

    switch (state) {
        case CreateAccountState.Agreement:
            return (
                <PlatformAgreementForm
                    onSubmit={confirmAgreement}
                    require={require}
                />
            );
        case CreateAccountState.Create:
            return (
                <ResetPasswordForm
                    onSubmit={handleSubmit}
                    mode={ResetPasswordMode.Create}
                />
            );
        case CreateAccountState.SetupOtp:
            return (
                <SetupOtpForm
                    secret={totpSecret?.base64 ?? ""}
                    qrCode={totpSecret?.qrCode ?? ""}
                    onSubmit={confirmOtp}
                    onBack={() => setState(CreateAccountState.Create)}
                />
            );
        case CreateAccountState.Redirecting:
            return <AccountSuccessRedirecting />;
    }
};

export default CreateAccountView;
